// Bridges era-web's persisted human-label answer values to the canonical
// snake_case enums the era-api gate validates (contract §4 / spec §10b).
// `toEnum` is idempotent: an already-canonical value passes through unchanged.

type VocabField =
	| "gender"
	| "fertilityStatus"
	| "fertilityRole"
	| "journeyType";

const LABEL_TO_ENUM: Record<VocabField, Record<string, string>> = {
	gender: {
		Female: "female",
		Male: "male",
		"Non-binary": "non_binary",
		Other: "other",
	},
	fertilityStatus: {
		"Trying to conceive": "trying_to_conceive",
		"Preserving fertility": "preserving_fertility",
		"Egg or Sperm Donor": "egg_sperm_donor",
		"Surrogacy Journey": "surrogacy",
		"Currently pregnant": "currently_pregnant",
		"Early parenting": "early_parenting",
	},
	fertilityRole: {
		"Primary patient": "primary_patient",
		Partner: "partner",
		"Support person": "support_person",
		"Exploring my options": "exploring_options",
		Surrogate: "surrogate",
		"Intended parent": "intended_parent",
		Parent: "parent",
	},
	journeyType: {
		IUI: "iui",
		IVF: "ivf",
		Surrogacy: "surrogacy",
		"Egg donation": "egg_donation",
		"Sperm donation": "sperm_donation",
		"Egg freezing": "egg_freezing",
		"I don't know yet": "dont_know",
		Other: "other",
	},
};

const ENUM_SETS: Record<VocabField, Set<string>> = {
	gender: new Set(Object.values(LABEL_TO_ENUM.gender)),
	fertilityStatus: new Set(Object.values(LABEL_TO_ENUM.fertilityStatus)),
	fertilityRole: new Set(Object.values(LABEL_TO_ENUM.fertilityRole)),
	journeyType: new Set(Object.values(LABEL_TO_ENUM.journeyType)),
};

export function toEnum(field: VocabField, value: string): string | undefined {
	if (ENUM_SETS[field].has(value)) return value; // already canonical
	// "Other - free text" custom journey entries collapse to "other".
	if (field === "journeyType" && value.startsWith("Other - ")) return "other";
	return LABEL_TO_ENUM[field][value];
}

export function toEnumArray(field: VocabField, values: string[]): string[] {
	return values
		.map((v) => toEnum(field, v))
		.filter((v): v is string => v !== undefined);
}

export const ENUM_TO_LABEL: Record<
	VocabField,
	Record<string, string>
> = Object.fromEntries(
	(Object.keys(LABEL_TO_ENUM) as VocabField[]).map((field) => [
		field,
		Object.fromEntries(
			Object.entries(LABEL_TO_ENUM[field]).map(([label, e]) => [e, label]),
		),
	]),
) as Record<VocabField, Record<string, string>>;

// Reverse direction for seeding the wizard from persisted data: the API stores
// canonical enums, but the screens select/highlight by label. Idempotent on
// labels and pass-through on unknown values (legacy strings stay untouched).
export function toLabel(field: VocabField, value: string): string {
	return ENUM_TO_LABEL[field][value] ?? value;
}

export function toLabelArray(field: VocabField, values: string[]): string[] {
	return values.map((v) => toLabel(field, v));
}
